import React, { useState, useEffect, useCallback } from 'react';
import { safeJson, networkMessage } from '../apiHelpers';

const C = {
  primary: '#0F6E56',
  border: '#E3E1D9',
  muted: '#5F5E5A',
  gold: '#91600C',
};

function authHeaders() {
  const token = localStorage.getItem('token') || '';
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Token ${token}` } : {}),
  };
}

/** Kisi user ka rank badge - naam ke sath lagta hai. */
export function RankBadge({ username, size = 10 }) {
  const [rank, setRank] = useState(null);

  useEffect(() => {
    if (!username) return;
    let alive = true;
    fetch(`/api/forum/rank/${encodeURIComponent(username)}/`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (alive && d && !d.error) setRank(d); })
      .catch(() => {});
    return () => { alive = false; };
  }, [username]);

  if (!rank) return null;
  const stars = rank.stars || 0;

  return (
    <span
      title={`${rank.rank} — activity ${rank.activity}, merit ${rank.merit}`}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 3, marginLeft: 6, whiteSpace: 'nowrap' }}
    >
      <span style={{ fontSize: size, color: C.muted, fontWeight: 600 }}>{rank.rank}</span>
      {stars > 0 && (
        <span style={{ fontSize: size - 1, color: stars >= 5 ? C.gold : C.primary, letterSpacing: -1 }}>
          {rank.icon.repeat(Math.min(stars, 7))}
        </span>
      )}
      {rank.merit > 0 && (
        <span style={{ fontSize: size - 1, color: C.gold }}>{'\u2B50'}{rank.merit}</span>
      )}
    </span>
  );
}

/**
 * ⭐ Give Merit ka button.
 * Apni post par nazar nahi aata, aur merit khatam ho to bhi nahi.
 */
export default function MeritButton({ postId, authorUsername, user, onDone }) {
  const [mine, setMine] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const loadMine = useCallback(() => {
    if (!user) return;
    fetch('/api/forum/merit/', { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d && !d.error) setMine(d); })
      .catch(() => {});
  }, [user]);

  useEffect(() => { loadMine(); }, [loadMine]);

  // apni post - button ka koi matlab nahi
  if (!user || user.username === authorUsername) return null;

  const available = mine ? (mine.merit_available || 0) : 0;

  const give = async () => {
    if (available <= 0) {
      setMsg('You have no merit available to give right now.');
      return;
    }
    const input = window.prompt(
      `How much merit? (you have ${available})\n\nMerit is limited — give it to posts that genuinely helped.`,
      '1'
    );
    if (input === null) return;
    const amount = parseInt(input, 10);
    if (!amount || amount < 1) { setMsg('Enter a number of 1 or more.'); return; }
    if (amount > available) { setMsg(`You only have ${available} merit available.`); return; }

    const reason = window.prompt('Why? (optional, shown to the author)') || '';

    setBusy(true);
    setMsg('');
    try {
      const res = await fetch('/api/forum/merit/give/', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ post_id: postId, amount, reason }),
      });
      const d = await safeJson(res);
      if (!res.ok) throw new Error(d.error || 'Could not give merit.');
      setMsg(`Sent ${amount} merit. You have ${d.remaining_available} left.`);
      loadMine();
      if (onDone) onDone();
    } catch (e) {
      setMsg(e.message);
    }
    setBusy(false);
  };

  return (
    <>
      <button
        onClick={give}
        disabled={busy}
        title={available > 0 ? `You have ${available} merit to give` : 'No merit available'}
        style={{
          padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: busy ? 'wait' : 'pointer',
          border: `1px solid ${available > 0 ? C.gold : C.border}`, borderRadius: 8,
          background: '#fff', color: available > 0 ? C.gold : '#B4B2A9',
        }}
      >
        {busy ? '…' : `\u2B50 Merit${available > 0 ? ` (${available})` : ''}`}
      </button>
      {msg && (
        <span style={{ fontSize: 11, color: msg.startsWith('Sent') ? C.primary : '#C0392B', marginLeft: 8 }}>
          {msg}
        </span>
      )}
    </>
  );
}
